'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AssetGraphQueryDto,
  AssetGraphResponseDto,
} from '@terra/shared-contracts-external-network';
import { mergeAssetGraphResponses } from '@terra/shared-contracts-external-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  buildExternalNetworkGraphQuery,
  EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH,
  EXPLORE_NEIGHBORHOOD_MAX_DEPTH,
  type ExternalNetworkGraphSelectedNode,
  type ExternalNetworkGraphViewMode,
} from '../../utils/external-network-graph.utils';
import { useExternalNetworkGraphViewModeStore } from '../../store/surfaceGraphViewModeStore';
import { EXTERNAL_NETWORK_GRAPH_KEY } from './useExternalNetworkGraph';

export type { ExternalNetworkGraphSelectedNode, ExternalNetworkGraphViewMode };

const FULL_VIEW_MODE = 'full' as const satisfies ExternalNetworkGraphViewMode;

const emptyGraph = (): AssetGraphResponseDto => ({
  nodes: [],
  edges: [],
  truncated: false,
  totalCount: 0,
});

export function useExternalNetworkExploreGraph({
  surfaceId,
}: {
  surfaceId: string | null;
}) {
  const queryClient = useQueryClient();
  const requestSeqRef = useRef(0);
  const hasAutoRunDefaultViewRef = useRef(false);
  const setViewModeForSurface = useExternalNetworkGraphViewModeStore(
    (state) => state.setViewMode,
  );

  const [neighborhoodRoot, setNeighborhoodRoot] =
    useState<ExternalNetworkGraphSelectedNode | null>(null);
  const [neighborhoodDepth, setNeighborhoodDepth] = useState(
    EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH,
  );
  const [expandedClusterIds, setExpandedClusterIds] = useState<string[]>([]);
  const [exploredGraph, setExploredGraph] =
    useState<AssetGraphResponseDto | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isReplacingGraph, setIsReplacingGraph] = useState(false);
  const [isExpandingNode, setIsExpandingNode] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchGraphQuery = useCallback(
    async (query: Partial<AssetGraphQueryDto>) =>
      queryClient.fetchQuery({
        queryKey: [EXTERNAL_NETWORK_GRAPH_KEY, surfaceId, query],
        queryFn: () =>
          terraProxyApiClient.getExternalNetworkGraph(surfaceId ?? '', query),
      }),
    [queryClient, surfaceId],
  );

  const query = useMemo(
    () =>
      buildExternalNetworkGraphQuery({
        viewMode: FULL_VIEW_MODE,
        neighborhoodRoot,
        neighborhoodDepth,
        expandedClusterIds,
      }),
    [neighborhoodRoot, neighborhoodDepth, expandedClusterIds],
  );

  useEffect(() => {
    requestSeqRef.current += 1;
    hasAutoRunDefaultViewRef.current = false;
    setNeighborhoodRoot(null);
    setNeighborhoodDepth(EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH);
    setExpandedClusterIds([]);
    setExploredGraph(null);
    setIsInitialLoading(true);
    setIsReplacingGraph(false);
    setIsExpandingNode(false);
    setIsError(false);
  }, [surfaceId]);

  useEffect(() => {
    if (!surfaceId || hasAutoRunDefaultViewRef.current) {
      return;
    }

    hasAutoRunDefaultViewRef.current = true;
    setViewModeForSurface({
      surfaceId,
      viewMode: FULL_VIEW_MODE,
    });
  }, [surfaceId, setViewModeForSurface]);

  useEffect(() => {
    if (!surfaceId) {
      setIsInitialLoading(false);

      return;
    }

    const seq = ++requestSeqRef.current;

    setIsReplacingGraph(true);
    setIsError(false);

    void (async () => {
      try {
        const nextGraph = await fetchGraphQuery(query);

        if (requestSeqRef.current !== seq) return;
        setExploredGraph((current) => {
          if (!current) {
            return nextGraph;
          }

          return mergeAssetGraphResponses(current, nextGraph);
        });
      } catch (error: unknown) {
        if (requestSeqRef.current !== seq) return;
        console.warn('[ExternalNetworkExploreGraph] Query failed', error);
        setIsError(true);
      } finally {
        if (requestSeqRef.current === seq) {
          setIsReplacingGraph(false);
          setIsInitialLoading(false);
        }
      }
    })();
  }, [query, surfaceId, fetchGraphQuery]);

  const startNeighborhood = useCallback(
    (node: ExternalNetworkGraphSelectedNode) => {
      setNeighborhoodRoot(node);
      setNeighborhoodDepth(EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH);
    },
    [],
  );

  const expandMore = useCallback(() => {
    setNeighborhoodDepth((currentDepth) =>
      Math.min(currentDepth + 1, EXPLORE_NEIGHBORHOOD_MAX_DEPTH),
    );
  }, []);

  const setClusterExpanded = useCallback(
    ({ clusterId, expanded }: { clusterId: string; expanded: boolean }) => {
      setExpandedClusterIds((current) =>
        expanded
          ? current.includes(clusterId)
            ? current
            : [...current, clusterId]
          : current.filter((id) => id !== clusterId),
      );
    },
    [],
  );

  const resetToSurfaceGraph = useCallback(() => {
    setNeighborhoodRoot(null);
    setNeighborhoodDepth(EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH);
    setExpandedClusterIds([]);
  }, []);

  const mergeNodeNeighborhood = useCallback(
    async (node: ExternalNetworkGraphSelectedNode): Promise<boolean> => {
      const seq = ++requestSeqRef.current;

      setIsExpandingNode(true);
      setIsError(false);

      try {
        const neighborhoodQuery = buildExternalNetworkGraphQuery({
          viewMode: FULL_VIEW_MODE,
          neighborhoodRoot: node,
          neighborhoodDepth: EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH,
          expandedClusterIds: [],
        });
        const neighborhood = await fetchGraphQuery(neighborhoodQuery);

        if (requestSeqRef.current !== seq) {
          return false;
        }

        setExploredGraph((current) => {
          const base = current ?? emptyGraph();

          return mergeAssetGraphResponses(base, neighborhood);
        });

        return true;
      } catch (error: unknown) {
        if (requestSeqRef.current !== seq) {
          return false;
        }

        console.warn('[ExternalNetworkExploreGraph] Expand node failed', error);
        setIsError(true);

        return false;
      } finally {
        if (requestSeqRef.current === seq) {
          setIsExpandingNode(false);
        }
      }
    },
    [fetchGraphQuery],
  );

  const expandSelectedNode = useCallback(
    async (nodeId: string): Promise<boolean> => {
      const node = exploredGraph?.nodes.find(
        (candidate) => candidate.id === nodeId,
      );

      if (!node || node.nodeType === 'cluster' || node.kind === 'WEAKNESS') {
        return false;
      }

      return mergeNodeNeighborhood({ id: node.id, kind: node.kind });
    },
    [exploredGraph, mergeNodeNeighborhood],
  );

  const isNeighborhoodActive = neighborhoodRoot !== null;
  const hasActiveExploreView =
    isNeighborhoodActive || expandedClusterIds.length > 0;

  return {
    graph: exploredGraph ?? undefined,
    isLoading: isInitialLoading && !exploredGraph,
    isFetching: isReplacingGraph || isExpandingNode,
    isReplacingGraph,
    isExpandingNode,
    isError,
    viewMode: FULL_VIEW_MODE,
    neighborhoodRoot,
    neighborhoodDepth,
    expandedClusterIds,
    setClusterExpanded,
    isNeighborhoodActive,
    hasActiveExploreView,
    startNeighborhood,
    expandMore,
    expandSelectedNode,
    resetToSurfaceGraph,
  };
}
